/**
 * Audit Log Page
 * Displays login/authentication audit trail with filtering and export capabilities
 */
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { APP_BASE_URL, LOGIN_AUDIT, GET_USER_ROLES } from "../../Common/EndPoints";
import DataTable from "../../Components/DataGrid/DataGrid";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import moment from "moment";
import {
  Box,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Button,
  Autocomplete,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  VpnKey as AuthIcon,
  Language as IpIcon,
  Clear as ClearIcon,
  Group as GroupIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import { extractAllowedUserRoleList } from "../../Utils/Utils";
import { getParsedLocalStorage } from "../../Utils/storage-utils";

// Types
type Params = {
  page: number;
  pageSize: number;
  // Text search filters (server-side LIKE queries)
  emailLike?: string | null;
  ipAddLike?: string | null;
  userNameLike?: string | null;
  // Dropdown filters
  userRole?: string | null;
  loginStatus?: string | null;
  authenticationBy?: string | null;
  // Date range filters (timestamps in milliseconds)
  fromTs?: number | null;
  toTs?: number | null;
};

type AuditLogEntry = {
  id: string;
  loginRequestDt: string;
  lastRequestDt: string;
  userEmail: string;
  authenticationBy: string;
  userName: string;
  userRole: string;
  ipAddr: string;
  ipAddress: string;
  ip_addr: string;
  ip: string;
  loginStatus: string;
};

type UserRole = {
  id: number;
  value: string;
  gazzettedOrNotGazzetted?: string;
};

type FilterState = {
  email: string;
  ipAddr: string;
  userRole: UserRole | null;
  loginStatus: string;
  authMethod: string;
  fromDate: moment.Moment | null;
  toDate: moment.Moment | null;
  userName: string;
};

// Login status options
const LOGIN_STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "SUCCESS", label: "Success" },
  { value: "FAILED", label: "Failed" },
  { value: "ACTIVE", label: "Active" },
];

// Auth method options (based on actual API values)
const AUTH_METHOD_OPTIONS = [
  { value: "", label: "All Methods" },
  { value: "EMAIL", label: "Email" },
  { value: "OTP", label: "OTP" },
  { value: "SSO", label: "SSO" },
];

type PaginationState = {
  pageIndex: number;
  pageSize: number;
  totalRowCount: number;
};

// Helper to clean params - remove null/undefined/empty values
function cleanParams(params: Params): Record<string, any> {
  const cleaned: Record<string, any> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      cleaned[key] = value;
    }
  });
  return cleaned;
}

// API function
async function getAuditLog(params: Params) {
  const cleanedParams = cleanParams(params);
  console.log("Audit Log - API Request params:", cleanedParams);
  const response = await axios.get(APP_BASE_URL + LOGIN_AUDIT, { params: cleanedParams });
  console.log("Audit Log - API Response:", response.data);
  return response.data.data;
}

// Helper to get IP address from various possible field names
const getIpAddress = (row: AuditLogEntry): string => {
  const ip = row.ipAddr || row.ipAddress || row.ip_addr || row.ip;
  // Handle "NA", "N/A", empty or undefined values
  if (!ip || ip === "NA" || ip === "N/A" || ip === "null" || ip === "undefined") {
    return "—";
  }
  return ip;
};

// Status chip component
const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const statusLower = status?.toLowerCase() || "";
  const isSuccess = statusLower === "success" || statusLower === "active";
  const isFailed = statusLower === "failed" || statusLower === "failure";

  return (
    <Chip
      icon={isSuccess ? <SuccessIcon /> : isFailed ? <ErrorIcon /> : undefined}
      label={status || "Unknown"}
      size="small"
      sx={{
        fontWeight: 500,
        fontSize: "0.75rem",
        ...(isSuccess && {
          backgroundColor: "var(--success-light)",
          color: "var(--success-dark)",
          "& .MuiChip-icon": { color: "var(--success-main)", fontSize: 16 },
        }),
        ...(isFailed && {
          backgroundColor: "var(--error-light)",
          color: "var(--error-dark)",
          "& .MuiChip-icon": { color: "var(--error-main)", fontSize: 16 },
        }),
        ...(!isSuccess && !isFailed && {
          backgroundColor: "var(--neutral-100)",
          color: "var(--neutral-600)",
        }),
      }}
    />
  );
};

// Auth method chip
const AuthMethodChip: React.FC<{ method: string }> = ({ method }) => {
  return (
    <Chip
      icon={<AuthIcon sx={{ fontSize: 14 }} />}
      label={method || "Standard"}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 500,
        fontSize: "0.75rem",
        borderColor: "var(--primary-200)",
        color: "var(--primary-700)",
        backgroundColor: "var(--primary-50)",
        "& .MuiChip-icon": { color: "var(--primary-600)" },
      }}
    />
  );
};

// Table columns configuration
const getColumns = (): GridColDef[] => [
  {
    field: "loginRequestDt",
    headerName: "Login Time",
    sortable: true,
    minWidth: 160,
    flex: 1,
    renderCell: (params: GridRenderCellParams) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ScheduleIcon sx={{ fontSize: 16, color: "var(--neutral-400)" }} />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color: "var(--neutral-800)", fontSize: "0.8125rem" }}>
            {moment(params.value).format("DD MMM YYYY")}
          </Typography>
          <Typography variant="caption" sx={{ color: "var(--neutral-500)", fontSize: "0.75rem" }}>
            {moment(params.value).format("HH:mm:ss")}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    field: "userName",
    headerName: "User",
    sortable: true,
    minWidth: 200,
    flex: 1.2,
    renderCell: (params: GridRenderCellParams) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: "var(--primary-100)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: 18, color: "var(--primary-600)" }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: "var(--neutral-800)", fontSize: "0.8125rem" }}>
            {params.value || "—"}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "var(--neutral-500)",
              fontSize: "0.75rem",
              maxWidth: 160,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
            title={params.row.userEmail}
          >
            {params.row.userEmail}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    field: "userRole",
    headerName: "Role",
    sortable: true,
    minWidth: 130,
    flex: 0.9,
    renderCell: (params: GridRenderCellParams) => (
      <Chip
        label={params.value || "—"}
        size="small"
        sx={{
          fontWeight: 500,
          fontSize: "0.75rem",
          backgroundColor: "var(--neutral-100)",
          color: "var(--neutral-700)",
        }}
      />
    ),
  },
  {
    field: "authenticationBy",
    headerName: "Auth Method",
    sortable: true,
    minWidth: 140,
    flex: 1,
    renderCell: (params: GridRenderCellParams) => <AuthMethodChip method={params.value} />,
  },
  {
    field: "ipAddress",
    headerName: "IP Address",
    sortable: true,
    minWidth: 150,
    flex: 1,
    valueGetter: (params: any) => getIpAddress(params.row),
    renderCell: (params: GridRenderCellParams) => {
      const ipAddress = getIpAddress(params.row);
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IpIcon sx={{ fontSize: 16, color: "var(--neutral-400)" }} />
          <Typography
            variant="body2"
            sx={{
              fontFamily: "'JetBrains Mono', 'Consolas', monospace",
              fontSize: "0.8125rem",
              color: "var(--neutral-700)",
            }}
          >
            {ipAddress}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: "loginStatus",
    headerName: "Status",
    sortable: true,
    minWidth: 120,
    flex: 0.8,
    renderCell: (params: GridRenderCellParams) => <StatusChip status={params.value} />,
  },
];

// Table info for export
const AUDIT_TABLE_INFO = {
  id: "audit-log-table",
  label: "Login Audit Log",
  requireAuthSign: false,
  timestamp: { startTs: null, endTs: null },
};

// Initial states
const initialPagination: PaginationState = {
  pageIndex: 0,
  pageSize: 20,
  totalRowCount: 0,
};

const initialFilters: FilterState = {
  email: "",
  ipAddr: "",
  userRole: null,
  loginStatus: "",
  authMethod: "",
  fromDate: null,
  toDate: null,
  userName: "",
};

// Client-side filter function
const applyClientFilters = (data: AuditLogEntry[], filters: FilterState): AuditLogEntry[] => {
  return data.filter((record) => {
    // Email filter (case-insensitive partial match)
    if (filters.email && !record.userEmail?.toLowerCase().includes(filters.email.toLowerCase())) {
      return false;
    }

    // User name filter (case-insensitive partial match)
    if (filters.userName && !record.userName?.toLowerCase().includes(filters.userName.toLowerCase())) {
      return false;
    }

    // IP address filter (partial match)
    if (filters.ipAddr && !record.ipAddr?.toLowerCase().includes(filters.ipAddr.toLowerCase())) {
      return false;
    }

    // User role filter (exact match)
    if (filters.userRole && record.userRole?.toLowerCase() !== filters.userRole.value?.toLowerCase()) {
      return false;
    }

    // Login status filter (exact match)
    if (filters.loginStatus && record.loginStatus?.toUpperCase() !== filters.loginStatus.toUpperCase()) {
      return false;
    }

    // Auth method filter (exact match)
    if (filters.authMethod && record.authenticationBy?.toUpperCase() !== filters.authMethod.toUpperCase()) {
      return false;
    }

    // Date range filter
    if (filters.fromDate || filters.toDate) {
      const recordDate = moment(record.loginRequestDt);
      if (filters.fromDate && recordDate.isBefore(filters.fromDate.startOf('day'))) {
        return false;
      }
      if (filters.toDate && recordDate.isAfter(filters.toDate.endOf('day'))) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Audit Log Component
 */
const AuditLog: React.FC = () => {
  const [allRows, setAllRows] = useState<AuditLogEntry[]>([]); // All fetched data
  const [rows, setRows] = useState<AuditLogEntry[]>([]); // Filtered data for display
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>(initialPagination);
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [debouncedFilters, setDebouncedFilters] = useState<FilterState>(filters);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);

  const columns = useMemo(() => getColumns(), []);

  // Get user roles from localStorage (role hierarchies)
  useEffect(() => {
    try {
      const roleHierarchies = getParsedLocalStorage("roleHierarchies", null);
      if (roleHierarchies) {
        const roles = extractAllowedUserRoleList(roleHierarchies);
        setUserRoles(roles);
      }
    } catch (error) {
      console.error("Failed to load user roles:", error);
    }
  }, []);

  // Debounce filter changes
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);
    return () => clearTimeout(handler);
  }, [filters]);

  // Fetch data from API (server-side pagination only)
  const getData = useCallback(async () => {
    setLoading(true);
    try {
      // Only send pagination params - API doesn't support filtering
      const response = await getAuditLog({
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        emailLike: null,
        ipAddLike: null,
        userNameLike: null,
        userRole: null,
        loginStatus: null,
        authenticationBy: null,
        fromTs: null,
        toTs: null,
      });

      const { content, totalElements } = response;
      const mappedData = content.map((record: any, index: number) => ({
        ...record,
        id: `${pagination.pageIndex}-${index}`,
      }));

      setAllRows(mappedData);
      setPagination((prev) => ({ ...prev, totalRowCount: totalElements }));
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      setAllRows([]);
      setPagination(initialPagination);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  // Apply client-side filters when data or filters change
  useEffect(() => {
    const hasFilters = !!(
      debouncedFilters.email ||
      debouncedFilters.userName ||
      debouncedFilters.ipAddr ||
      debouncedFilters.userRole ||
      debouncedFilters.loginStatus ||
      debouncedFilters.authMethod ||
      debouncedFilters.fromDate ||
      debouncedFilters.toDate
    );

    if (hasFilters) {
      const filtered = applyClientFilters(allRows, debouncedFilters);
      setRows(filtered);
    } else {
      setRows(allRows);
    }
  }, [allRows, debouncedFilters]);

  useEffect(() => {
    getData();
  }, [getData]);

  // Handlers
  const handleTextFilterChange = useCallback(
    (field: "email" | "ipAddr" | "userName") => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilters((prev) => ({ ...prev, [field]: event.target.value }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    []
  );

  const handleRoleChange = useCallback((_event: any, value: UserRole | null) => {
    setFilters((prev) => ({ ...prev, userRole: value }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleStatusChange = useCallback((event: any) => {
    setFilters((prev) => ({ ...prev, loginStatus: event.target.value }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleAuthMethodChange = useCallback((event: any) => {
    setFilters((prev) => ({ ...prev, authMethod: event.target.value }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleDateChange = useCallback((field: "fromDate" | "toDate") => (value: unknown) => {
    setFilters((prev) => ({ ...prev, [field]: value as moment.Moment | null }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(initialFilters);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleRefresh = useCallback(() => {
    getData();
  }, [getData]);

  const handlePageChange = useCallback((index: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: index }));
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPagination((prev) => ({ ...prev, pageSize: size, pageIndex: 0 }));
  }, []);

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.email ||
      filters.ipAddr ||
      filters.userRole ||
      filters.loginStatus ||
      filters.authMethod ||
      filters.fromDate ||
      filters.toDate ||
      filters.userName
    );
  }, [filters]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        backgroundColor: "var(--color-bg)",
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontSize: "1.25rem",
              fontWeight: 600,
              color: "var(--neutral-800)",
            }}
          >
            Login Audit Log
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "var(--neutral-500)", mt: 0.5 }}
          >
            Track user authentication activity and login attempts
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh data">
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                backgroundColor: "var(--color-white)",
                border: "1px solid var(--border-color)",
                borderRadius: "var(--radius-md)",
                "&:hover": {
                  backgroundColor: "var(--neutral-50)",
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: "var(--color-white)",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border-color)",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          {/* First Row - Date Range and Status Filters */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <FilterIcon sx={{ color: "var(--neutral-400)", fontSize: 20 }} />

            {/* From Date */}
            <DatePicker
              label="From Date"
              value={filters.fromDate}
              onChange={handleDateChange("fromDate")}
              maxDate={filters.toDate || moment()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ minWidth: 160 }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon sx={{ fontSize: 18, color: "var(--neutral-400)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            {/* To Date */}
            <DatePicker
              label="To Date"
              value={filters.toDate}
              onChange={handleDateChange("toDate")}
              minDate={filters.fromDate || undefined}
              maxDate={moment()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ minWidth: 160 }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon sx={{ fontSize: 18, color: "var(--neutral-400)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            {/* Login Status */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.loginStatus}
                onChange={handleStatusChange}
                label="Status"
              >
                {LOGIN_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Auth Method */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Auth Method</InputLabel>
              <Select
                value={filters.authMethod}
                onChange={handleAuthMethodChange}
                label="Auth Method"
              >
                {AUTH_METHOD_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Results Count */}
            <Box sx={{ ml: "auto" }}>
              <Typography variant="body2" sx={{ color: "var(--neutral-500)", fontSize: "0.8125rem" }}>
                {hasActiveFilters ? (
                  <>
                    <strong>{rows.length}</strong> of {allRows.length} shown
                    <Typography component="span" sx={{ color: "var(--neutral-400)", fontSize: "0.75rem", ml: 1 }}>
                      (Total: {pagination.totalRowCount.toLocaleString()})
                    </Typography>
                  </>
                ) : (
                  <>
                    <strong>{pagination.totalRowCount.toLocaleString()}</strong> records found
                  </>
                )}
              </Typography>
            </Box>
          </Box>

          {/* Second Row - Text Search Filters */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              pl: 4, // Indent to align with first row filters
            }}
          >
            {/* User Role Filter */}
            <Autocomplete
              options={userRoles}
              getOptionLabel={(option) => option.value || ""}
              value={filters.userRole}
              onChange={handleRoleChange}
              size="small"
              sx={{ minWidth: 180 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="User Role"
                  placeholder="Filter by role..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <GroupIcon sx={{ fontSize: 18, color: "var(--neutral-400)" }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            {/* User Name Filter */}
            <TextField
              label="User Name"
              placeholder="Search by name..."
              value={filters.userName}
              onChange={handleTextFilterChange("userName")}
              size="small"
              sx={{ minWidth: 180 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ fontSize: 18, color: "var(--neutral-400)" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Email Filter */}
            <TextField
              label="Email"
              placeholder="Search by email..."
              value={filters.email}
              onChange={handleTextFilterChange("email")}
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "var(--neutral-400)" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* IP Address Filter */}
            <TextField
              label="IP Address"
              placeholder="Search by IP..."
              value={filters.ipAddr}
              onChange={handleTextFilterChange("ipAddr")}
              size="small"
              sx={{ minWidth: 160 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IpIcon sx={{ fontSize: 18, color: "var(--neutral-400)" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearFilters}
                startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                sx={{
                  color: "var(--neutral-600)",
                  "&:hover": {
                    backgroundColor: "var(--neutral-100)",
                  },
                }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </LocalizationProvider>
      </Paper>

      {/* Data Table */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          backgroundColor: "var(--color-white)",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border-color)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 400,
        }}
      >
        <DataTable
          tableInfo={AUDIT_TABLE_INFO}
          loading={loading}
          rowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          paginationMode="server"
          pageChange={handlePageChange}
          pageSize={pagination.pageSize}
          pageSizeChange={handlePageSizeChange}
          rows={rows}
          columns={columns}
          showFilterButton={false}
          getRowHeight={() => 64}
        />
      </Paper>
    </Box>
  );
};

export default AuditLog;
